import {
  type IChatAdminMessage,
  IChatData,
  IChatDataResponse,
  type IChatLiteMessage,
  type IChatResponse,
  IMeta,
  IMetaWrapper,
  useUpFetch,
} from '@repo/sdk'

interface ISendMessageResponse {
  message: string
}

export const useChatApi = () => {
  const upFetch = useUpFetch()

  const getMessagesForLoad = async (chatId: number, page = 1) => {
    return upFetch<IChatResponse>(`/loads/chats/${chatId}/messages`, {
      params: {
        i: 'user|last_message',
        page,
        perPage: 15,
        'sort[created_at]': 'asc',
      },
    })
  }

  const sendMessage = async (chatId: number, message: string) => {
    return upFetch<ISendMessageResponse>(`/loads/chats/${chatId}/messages`, {
      body: {
        message,
      },
      method: 'POST',
    })
  }

  const markChatMessagesAsRead = async (chatId: number) => {
    await upFetch(`/loads/chats/${chatId}/messages/read`, {
      method: 'PATCH',
    })
  }

  return {
    getMessagesForLoad,
    markChatMessagesAsRead,
    sendMessage,
  }
}

export const useChatAdminAdminInLoad = () => {
  const upFetch = useUpFetch()

  const sendMessage = async (load_id: number, message: string) => {
    return upFetch<ISendMessageResponse>('/loads/comments', {
      body: {
        content: message,
        load_id,
      },
      method: 'POST',
    })
  }

  const getMessages = async (chatId: number, page = 1): Promise<IChatResponse> => {
    const res = await upFetch<IMetaWrapper<IChatAdminMessage>>(`loads/${chatId}/comments`, {
      params: {
        page,
        perPage: 15,
        'sort[created_at]': 'asc',
      },
    })

    return {
      data: res.data.map(data => ({
        ...data,
        message: data.content,
      })) as unknown as IChatLiteMessage[],
      meta: res.meta,
    }
  }

  return {
    getMessages,
    sendMessage,
  }
}

export const useChatShipperAdminInLoad = () => {
  const upFetch = useUpFetch()

  const getMessages = async (
    chatId: number,
    params?: IMeta | { current_page: number; per_page?: number },
  ) => {
    return upFetch<IChatResponse>(`/loads/chats/${chatId}/messages`, {
      params: {
        i: 'user',
        page: params?.current_page,
        perPage: params?.per_page || 15,
        'sort[created_at]': 'asc',
      },
    })
  }

  const sendMessage = async (chatId: number, message: string) => {
    return upFetch<ISendMessageResponse>(`/loads/chats/${chatId}/messages`, {
      body: {
        message,
      },
      method: 'POST',
    })
  }

  const joinTo = async (chatId: number) => {
    try {
      await upFetch<boolean>('/loads/chats/join', {
        body: {
          chat_id: chatId,
        },
        method: 'POST',
      })

      return true
    } catch (error) {
      console.error(error)

      return false
    }
  }

  const getData = async (loadId: number): Promise<IChatData | undefined | void> => {
    const res = await upFetch<IChatDataResponse>(`/loads/users/chats?load_id=${loadId}&i=is_joined`)

    return res.data?.[0]
  }

  return { getData, getMessages, joinTo, sendMessage }
}

export const useMarkChatMessagesAsRead = () => {
  const upFetch = useUpFetch()

  return {
    markChatMessagesAsRead: async (chatId: number) => {
      await upFetch(`loads/chats/${chatId}/messages/read`, {
        method: 'PATCH',
      })
    },
  }
}
